import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import routeVariants from "../../assets/animates/routeVariants";
import Footer from "../global/Footer";
import NavbarBottom from "../global/NavbarBottom";
import guideCopy, { pickLang } from "./data/guideCopy";
import dangNhap from "../../assets/images/user-guide/dang-nhap.png";
import bamDangKy from "../../assets/images/user-guide/bam-dang-ky.png";
import dienThongTinDangKy from "../../assets/images/user-guide/dien-thong-tin-dang-ky.png";
import chiaSeThongTin from "../../assets/images/user-guide/chia-se-thong-tin.png";
import muaGoi from "../../assets/images/user-guide/mua-goi.png";
import thanhToan from "../../assets/images/user-guide/thanh-toan.png";
import xacNhanThanhToan from "../../assets/images/user-guide/xac-nhan-thanh-toan.png";
import hoanTatKiemTra from "../../assets/images/user-guide/hoan-tat-kiem-tra.png";
import taoTaiLieuMenu from "../../assets/images/user-guide/tao-tai-lieu-menu.png";
import taoTaiLieuBatDau from "../../assets/images/user-guide/tao-tai-lieu-bat-dau.png";
import taoTaiLieuTaiLen from "../../assets/images/user-guide/tao-tai-lieu-tai-len.png";
import taoTaiLieuQuyTrinhKy from "../../assets/images/user-guide/tao-tai-lieu-quy-trinh-ky.png";
import taoTaiLieuNguoiThamGia from "../../assets/images/user-guide/tao-tai-lieu-nguoi-tham-gia.png";
import taoTaiLieuTuyChon from "../../assets/images/user-guide/tao-tai-lieu-tuy-chon.png";
import taoTaiLieuTiepTucChinhSua from "../../assets/images/user-guide/tao-tai-lieu-tiep-tuc-chinh-sua.png";
import soanThaoNguoiThamGia from "../../assets/images/user-guide/soan-thao-nguoi-tham-gia.png";
import soanThaoThemTruong from "../../assets/images/user-guide/soan-thao-them-truong.png";
import soanThaoChiaSeNgay from "../../assets/images/user-guide/soan-thao-chia-se-ngay.png";
import theoDoiTongQuan from "../../assets/images/user-guide/theo-doi-tong-quan.png";
import theoDoiNhatKy from "../../assets/images/user-guide/theo-doi-nhat-ky.png";
import "./UserGuideContent.css";

const GUIDE_IMAGES = {
  "dang-nhap.png": dangNhap,
  "bam-dang-ky.png": bamDangKy,
  "dien-thong-tin-dang-ky.png": dienThongTinDangKy,
  "chia-se-thong-tin.png": chiaSeThongTin,
  "mua-goi.png": muaGoi,
  "thanh-toan.png": thanhToan,
  "xac-nhan-thanh-toan.png": xacNhanThanhToan,
  "hoan-tat-kiem-tra.png": hoanTatKiemTra,
  "tao-tai-lieu-menu.png": taoTaiLieuMenu,
  "tao-tai-lieu-bat-dau.png": taoTaiLieuBatDau,
  "tao-tai-lieu-tai-len.png": taoTaiLieuTaiLen,
  "tao-tai-lieu-quy-trinh-ky.png": taoTaiLieuQuyTrinhKy,
  "tao-tai-lieu-nguoi-tham-gia.png": taoTaiLieuNguoiThamGia,
  "tao-tai-lieu-tuy-chon.png": taoTaiLieuTuyChon,
  "tao-tai-lieu-tiep-tuc-chinh-sua.png": taoTaiLieuTiepTucChinhSua,
  "soan-thao-nguoi-tham-gia.png": soanThaoNguoiThamGia,
  "soan-thao-them-truong.png": soanThaoThemTruong,
  "soan-thao-chia-se-ngay.png": soanThaoChiaSeNgay,
  "theo-doi-tong-quan.png": theoDoiTongQuan,
  "theo-doi-nhat-ky.png": theoDoiNhatKy,
};

const DEFAULT_OPEN = { "create-contract": true };

const richText = (text) => {
  if (!text) return null;
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, index) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={index}>{part.slice(2, -2)}</strong>;
    }
    return <React.Fragment key={index}>{part}</React.Fragment>;
  });
};

const CaretIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9 6l6 6-6 6" />
  </svg>
);

const GuideVideo = ({ title, youtubeId }) => (
  <div className="user-guide-video">
    <iframe
      src={`https://www.youtube-nocookie.com/embed/${youtubeId}`}
      title={title}
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      allowFullScreen
      referrerPolicy="strict-origin-when-cross-origin"
    />
  </div>
);

const GuideImage = ({ src, alt, caption }) => {
  if (src) {
    return (
      <figure className="user-guide-img is-real">
        <img src={src} alt={alt} />
      </figure>
    );
  }

  return (
    <figure className="user-guide-img">
      <div className="user-guide-media-inner">
        <svg className="user-guide-media-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
          <rect x="3" y="5" width="18" height="14" rx="2" />
          <path d="M9 13l2.2-2.5L14 13l2-2.2 3 3.2" />
          <circle cx="8" cy="9" r="1.2" />
        </svg>
        <span>{alt}</span>
      </div>
      {caption ? <figcaption className="user-guide-caption">{caption}</figcaption> : null}
    </figure>
  );
};

const UserGuideContent = () => {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || "").toLowerCase().startsWith("en") ? "en" : "vi";
  const [openIds, setOpenIds] = useState(DEFAULT_OPEN);
  const [activeHref, setActiveHref] = useState("#register");
  const [tocMenuOpen, setTocMenuOpen] = useState(false);

  const toc = {
    register: pickLang(guideCopy.toc.register, lang),
    createContract: pickLang(guideCopy.toc.createContract, lang),
    basicContract: pickLang(guideCopy.toc.basicContract, lang),
    newDocument: pickLang(guideCopy.toc.newDocument, lang),
    draftDocument: pickLang(guideCopy.toc.draftDocument, lang),
    trackDocument: pickLang(guideCopy.toc.trackDocument, lang),
  };
  const register = guideCopy.register;
  const contract = guideCopy.contract;

  useEffect(() => {
    setOpenIds({ ...DEFAULT_OPEN });
  }, [lang]);

  useEffect(() => {
    const hrefs = [
      "#register",
      "#create-contract",
      "#basic-contract",
      "#new-document",
      "#draft-document",
      "#track-document",
    ];

    const setActive = () => {
      const y = window.scrollY + 140;
      let current = hrefs[0];
      hrefs.forEach((href) => {
        const section = document.querySelector(href);
        if (section && section.offsetTop <= y) current = href;
      });
      setActiveHref(current);
    };

    setActive();
    window.addEventListener("scroll", setActive, { passive: true });
    return () => window.removeEventListener("scroll", setActive);
  }, []);

  const isOpen = (id) => !!openIds[id];

  const toggleGroup = (event, id) => {
    event.preventDefault();
    event.stopPropagation();
    setOpenIds((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const expandGroup = (id) => {
    setOpenIds((prev) => ({ ...prev, [id]: true }));
  };

  const closeTocMenu = () => setTocMenuOpen(false);

  const onTocLinkClick = (expandId) => {
    if (expandId) expandGroup(expandId);
    closeTocMenu();
  };

  const caretLabel = (open, name) =>
    t(open ? "guide-page.collapse" : "guide-page.expand", { name });

  const activeTocLabel = {
    "#register": toc.register,
    "#create-contract": toc.createContract,
    "#basic-contract": toc.basicContract,
    "#new-document": toc.newDocument,
    "#draft-document": toc.draftDocument,
    "#track-document": toc.trackDocument,
  }[activeHref] || toc.register;

  return (
    <motion.div
      variants={routeVariants}
      initial="initial"
      animate="final"
      id="content"
      className="fs-navigation-overlay-content fs-navigation-overlay-left-content fs-navigation__0 no-transform user-guide"
    >
      <div className="container-fuild background-solution-image">
        <div className="container px-md-0 pt-50 pb-50">
          <div className="row">
            <div className="col-md-12 text-center">
              <h2 className="text-center font-weight-bold mx-auto text-yellow font-60">
                {t("guide-page.heading")}
              </h2>
              <p className="mt-25 mb-20 text-white user-guide-hero-lead">
                {t("guide-page.description")}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="solutions-page bg-color-solution user-guide-page">
        <div className="container px-md-0">
          <div className="user-guide-wrap">
            <aside
              className={`user-guide-toc${tocMenuOpen ? " is-menu-open" : ""}`}
              aria-label={t("guide-page.toc")}
            >
              <div className="user-guide-toc-inner">
              <button
                type="button"
                className="user-guide-toc-toggle"
                aria-expanded={tocMenuOpen}
                aria-controls="user-guide-toc-nav"
                aria-label={t(tocMenuOpen ? "guide-page.toc-close" : "guide-page.toc-open")}
                onClick={() => setTocMenuOpen((open) => !open)}
              >
                <span className="user-guide-toc-kicker">{t("guide-page.toc")}</span>
                <span className="user-guide-toc-current">{activeTocLabel}</span>
                <span className="user-guide-toc-toggle-caret" aria-hidden="true">
                  <CaretIcon />
                </span>
              </button>
              <h2 className="user-guide-toc-title">{t("guide-page.toc")}</h2>
              <nav id="user-guide-toc-nav" className="user-guide-toc-nav">
                <a
                  href="#register"
                  className={activeHref === "#register" ? "is-active" : undefined}
                  onClick={closeTocMenu}
                >
                  {toc.register}
                </a>
                <div className={`user-guide-group${isOpen("create-contract") ? " is-open" : ""}`}>
                  <div className="user-guide-toc-parent">
                    <a
                      href="#create-contract"
                      className={activeHref === "#create-contract" ? "is-active" : undefined}
                      onClick={() => onTocLinkClick("create-contract")}
                    >
                      <button
                        type="button"
                        className="user-guide-caret"
                        aria-expanded={isOpen("create-contract")}
                        aria-label={caretLabel(isOpen("create-contract"), toc.createContract)}
                        onClick={(event) => toggleGroup(event, "create-contract")}
                      >
                        <CaretIcon />
                      </button>
                      {toc.createContract}
                    </a>
                  </div>
                  <div className="user-guide-toc-children">
                    <div className={`user-guide-group${isOpen("basic-contract") ? " is-open" : ""}`}>
                      <div className="user-guide-toc-parent">
                        <a
                          className={`sub${activeHref === "#basic-contract" ? " is-active" : ""}`}
                          href="#basic-contract"
                          onClick={() => onTocLinkClick("basic-contract")}
                        >
                          <button
                            type="button"
                            className="user-guide-caret"
                            aria-expanded={isOpen("basic-contract")}
                            aria-label={caretLabel(isOpen("basic-contract"), toc.basicContract)}
                            onClick={(event) => toggleGroup(event, "basic-contract")}
                          >
                            <CaretIcon />
                          </button>
                          {toc.basicContract}
                        </a>
                      </div>
                      <div className="user-guide-toc-children">
                        <a
                          className={`sub${activeHref === "#new-document" ? " is-active" : ""}`}
                          href="#new-document"
                          onClick={closeTocMenu}
                        >
                          {toc.newDocument}
                        </a>
                        <a
                          className={`sub${activeHref === "#draft-document" ? " is-active" : ""}`}
                          href="#draft-document"
                          onClick={closeTocMenu}
                        >
                          {toc.draftDocument}
                        </a>
                        <a
                          className={`sub${activeHref === "#track-document" ? " is-active" : ""}`}
                          href="#track-document"
                          onClick={closeTocMenu}
                        >
                          {toc.trackDocument}
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </nav>
              </div>
            </aside>

            <div className="user-guide-content">
              <section className="user-guide-section" id="register">
                <span className="user-guide-label">
                  <span className="user-guide-num">01</span> {pickLang(register.label, lang)}
                </span>
                <h2 className="user-guide-title">{pickLang(register.title, lang)}</h2>
                <p className="user-guide-lead">{pickLang(register.lead, lang)}</p>
                <GuideVideo
                  title={pickLang(register.videoLabel, lang)}
                  youtubeId="JXc5tqhH9Ck"
                />
                {register.steps.map((step) => (
                  <article className="user-guide-step" key={pickLang(step.label, lang)}>
                    <p className="user-guide-step-line">
                      <strong>
                        {pickLang(step.label, lang)}. {pickLang(step.title, lang)}
                      </strong>
                    </p>
                    <ul>
                      {pickLang(step.items, lang).map((item) => {
                        if (typeof item === "string") {
                          return <li key={item}>{richText(item)}</li>;
                        }
                        return (
                          <li key={item.text}>
                            {richText(item.text)}
                            <ul>
                              {item.children.map((child) => (
                                <li key={child}>{child}</li>
                              ))}
                            </ul>
                          </li>
                        );
                      })}
                    </ul>
                    {step.images?.map((image) => (
                      <GuideImage
                        key={image.file}
                        src={GUIDE_IMAGES[image.file]}
                        alt={pickLang(image.alt, lang)}
                        caption={`Placeholder — ${image.file}`}
                      />
                    ))}
                  </article>
                ))}
              </section>

              <section className="user-guide-section" id="create-contract">
                <span className="user-guide-label">
                  <span className="user-guide-num">02</span> {pickLang(contract.label, lang)}
                </span>
                <h2 className="user-guide-title">{pickLang(contract.title, lang)}</h2>
                <GuideVideo
                  title={pickLang(contract.videoLabel, lang)}
                  youtubeId="dzl6dkjzC-E"
                />

                <div className="user-guide-topic">
                  <p className="user-guide-group-label" id="basic-contract">
                    {pickLang(contract.basicTitle, lang)}
                  </p>
                  <h3 className="user-guide-sub" id="new-document">
                    {pickLang(contract.newDocTitle, lang)}
                  </h3>

                  {contract.createSteps.map((step) => (
                    <article className="user-guide-step" key={pickLang(step.label, lang) + pickLang(step.body, lang)}>
                      <p className="user-guide-step-line">
                        <strong>{pickLang(step.label, lang)}:</strong>{" "}
                        {richText(pickLang(step.body, lang))}
                      </p>
                      {step.items ? (
                        <ul>
                          {pickLang(step.items, lang).map((item) => (
                            <li key={item}>{richText(item)}</li>
                          ))}
                        </ul>
                      ) : null}
                      {step.modes ? (
                        <div className="user-guide-mode-grid">
                          {step.modes.map((mode) => (
                            <div className="user-guide-mode-card" key={mode.num}>
                              <span className="user-guide-badge">{mode.num}</span>
                              <h4>{pickLang(mode.title, lang)}</h4>
                              <p>{pickLang(mode.body, lang)}</p>
                            </div>
                          ))}
                        </div>
                      ) : null}
                      {step.alt || step.file ? (
                        <GuideImage
                          src={GUIDE_IMAGES[step.file]}
                          alt={pickLang(step.alt, lang)}
                          caption={pickLang(step.caption, lang)}
                        />
                      ) : null}
                    </article>
                  ))}

                  <h3 className="user-guide-sub" id="draft-document">
                    {pickLang(contract.draftTitle, lang)}
                  </h3>

                  {contract.draftSteps.map((step) => (
                    <article className="user-guide-step" key={pickLang(step.label, lang) + pickLang(step.body, lang)}>
                      <p className="user-guide-step-line">
                        <strong>{pickLang(step.label, lang)}:</strong>{" "}
                        {richText(pickLang(step.body, lang))}
                      </p>
                      {step.alt || step.file ? (
                        <GuideImage
                          src={GUIDE_IMAGES[step.file]}
                          alt={pickLang(step.alt, lang)}
                          caption={pickLang(step.caption, lang)}
                        />
                      ) : null}
                    </article>
                  ))}

                  <h3 className="user-guide-sub" id="track-document">
                    {pickLang(contract.trackTitle, lang)}
                  </h3>
                  <p className="user-guide-lead">{pickLang(contract.trackLead, lang)}</p>
                  {contract.trackMethods.map((method) => (
                    <article className="user-guide-step" key={pickLang(method.label, lang)}>
                      <p className="user-guide-step-line">
                        <strong>{pickLang(method.label, lang)}:</strong>{" "}
                        {richText(pickLang(method.body, lang))}
                      </p>
                      <GuideImage
                        src={GUIDE_IMAGES[method.file]}
                        alt={pickLang(method.alt, lang)}
                        caption={pickLang(method.caption, lang)}
                      />
                    </article>
                  ))}
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>

      <Footer />
      <NavbarBottom />
    </motion.div>
  );
};

export default UserGuideContent;
